/**
 * Continent silhouettes backed by the imported Risk SVG territory paths.
 *
 * Each continent path is a compound path made by joining its territory paths.
 * That keeps clipping and background silhouettes aligned with the actual
 * imported board geometry instead of the previous generated layout.
 */

import { riskPath } from './riskMapSvgPaths.js';
import type { ModuleId } from './types.js';

export type ContinentId =
  | 'north-america'
  | 'south-america'
  | 'europe'
  | 'africa'
  | 'asia'
  | 'australia';

export interface Continent {
  readonly id: ContinentId;
  readonly displayName: string;
  readonly pathD: string;
  readonly labelAnchor: { readonly x: number; readonly y: number };
  readonly modulesPresent: readonly ModuleId[];
}

export const CONTINENTS: readonly Continent[] = [
  {
    id: "north-america" as ContinentId,
    displayName: "North America",
    pathD: riskPath("frontend-alaska") + ' ' + riskPath("frontend-nwt") + ' ' + riskPath("frontend-greenland") + ' ' + riskPath("frontend-alberta") + ' ' + riskPath("frontend-ontario") + ' ' + riskPath("frontend-quebec") + ' ' + riskPath("frontend-western-us") + ' ' + riskPath("frontend-eastern-us") + ' ' + riskPath("frontend-central-america"),
    labelAnchor: { x: 205, y: 165 },
    modulesPresent: ["frontend"] as readonly ModuleId[],
  },
  {
    id: "south-america" as ContinentId,
    displayName: "South America",
    pathD: riskPath("devops-venezuela") + ' ' + riskPath("devops-peru") + ' ' + riskPath("devops-brazil") + ' ' + riskPath("devops-argentina"),
    labelAnchor: { x: 295, y: 465 },
    modulesPresent: ["devops"] as readonly ModuleId[],
  },
  {
    id: "europe" as ContinentId,
    displayName: "Europe",
    pathD: riskPath("compliance-iceland") + ' ' + riskPath("compliance-great-britain") + ' ' + riskPath("compliance-scandinavia") + ' ' + riskPath("compliance-northern-europe") + ' ' + riskPath("compliance-western-europe") + ' ' + riskPath("compliance-southern-europe") + ' ' + riskPath("compliance-ukraine"),
    labelAnchor: { x: 500, y: 215 },
    modulesPresent: ["compliance"] as readonly ModuleId[],
  },
  {
    id: "africa" as ContinentId,
    displayName: "Africa",
    pathD: riskPath("security-north-africa") + ' ' + riskPath("security-egypt") + ' ' + riskPath("security-east-africa") + ' ' + riskPath("security-congo") + ' ' + riskPath("security-south-africa") + ' ' + riskPath("security-madagascar"),
    labelAnchor: { x: 525, y: 430 },
    modulesPresent: ["security"] as readonly ModuleId[],
  },
  {
    id: "asia" as ContinentId,
    displayName: "Asia",
    pathD: riskPath("backend-ural") + ' ' + riskPath("backend-siberia") + ' ' + riskPath("backend-yakutsk") + ' ' + riskPath("backend-kamchatka") + ' ' + riskPath("backend-irkutsk") + ' ' + riskPath("backend-mongolia") + ' ' + riskPath("backend-japan") + ' ' + riskPath("backend-afghanistan") + ' ' + riskPath("backend-china") + ' ' + riskPath("backend-middle-east") + ' ' + riskPath("backend-india") + ' ' + riskPath("backend-siam"),
    labelAnchor: { x: 785, y: 215 },
    modulesPresent: ["backend"] as readonly ModuleId[],
  },
  {
    id: "australia" as ContinentId,
    displayName: "Australia",
    pathD: riskPath("database-indonesia") + ' ' + riskPath("database-new-guinea") + ' ' + riskPath("database-western-australia") + ' ' + riskPath("database-eastern-australia"),
    labelAnchor: { x: 840, y: 515 },
    modulesPresent: ["database"] as readonly ModuleId[],
  },
];

export const CONTINENTS_BY_ID: ReadonlyMap<ContinentId, Continent> = new Map(
  CONTINENTS.map((c) => [c.id, c]),
);

export const TERRITORY_TO_CONTINENT: Readonly<Record<string, ContinentId>> = {
  "frontend-alaska": "north-america",
  "frontend-nwt": "north-america",
  "frontend-greenland": "north-america",
  "frontend-alberta": "north-america",
  "frontend-ontario": "north-america",
  "frontend-quebec": "north-america",
  "frontend-western-us": "north-america",
  "frontend-eastern-us": "north-america",
  "frontend-central-america": "north-america",
  "devops-venezuela": "south-america",
  "devops-peru": "south-america",
  "devops-brazil": "south-america",
  "devops-argentina": "south-america",
  "compliance-iceland": "europe",
  "compliance-great-britain": "europe",
  "compliance-scandinavia": "europe",
  "compliance-northern-europe": "europe",
  "compliance-western-europe": "europe",
  "compliance-southern-europe": "europe",
  "compliance-ukraine": "europe",
  "security-north-africa": "africa",
  "security-egypt": "africa",
  "security-east-africa": "africa",
  "security-congo": "africa",
  "security-south-africa": "africa",
  "security-madagascar": "africa",
  "backend-ural": "asia",
  "backend-siberia": "asia",
  "backend-yakutsk": "asia",
  "backend-kamchatka": "asia",
  "backend-irkutsk": "asia",
  "backend-mongolia": "asia",
  "backend-japan": "asia",
  "backend-afghanistan": "asia",
  "backend-china": "asia",
  "backend-middle-east": "asia",
  "backend-india": "asia",
  "backend-siam": "asia",
  "database-indonesia": "australia",
  "database-new-guinea": "australia",
  "database-western-australia": "australia",
  "database-eastern-australia": "australia",
};
